import initials from "initials";
import { types } from "mobx-state-tree";
import { isAuthenticatedTeacher } from "../../lib/auth";
import { UserTypeEnum } from "./user-types";
export const UserPortalOffering = types
    .model("UserPortalOffering", {
    classId: "",
    classHash: "",
    className: "",
    classUrl: "",
    teacher: "",
    activityTitle: "",
    activityUrl: "",
    problemOrdinal: "",
    unitCode: "",
    offeringId: "",
    location: ""
})
    .views(self => ({
    get problemPath() {
        const separator = self.unitCode && self.problemOrdinal ? "/" : "";
        return `${self.unitCode}${separator}${self.problemOrdinal.replace(".", "/")}`;
    }
}));
const undefinedStandaloneAuth = undefined;
const undefinedStandaloneAuthUser = undefined;
export const UserModel = types
    .model("User", {
    authenticated: false,
    id: "0",
    type: types.maybe(UserTypeEnum),
    name: "Anonymous User",
    className: "",
    classHash: "",
    offeringId: "",
    // This is the last group the user joined. It is synced with the latestGroupId
    // property in Firebase
    latestGroupId: types.maybe(types.string),
    // This is the group of this user in the particular offering that is being
    // run right now. This is the property you usually will want to use.
    // latestGroupId could be referring to a group from a different assignment/offering
    currentGroupId: types.maybe(types.string),
    portal: "",
    network: types.maybe(types.string),
    networks: types.array(types.string),
    loggingRemoteEndpoint: types.maybe(types.string),
    portalClassOfferings: types.array(UserPortalOffering),
    demoClassHashes: types.array(types.string),
    lastSupportViewTimestamp: types.maybe(types.number),
    lastStickyNoteViewTimestamp: types.maybe(types.number)
})
    .volatile(self => ({
    // in standalone mode, this is defined with a state value
    standaloneAuth: undefinedStandaloneAuth,
    // at the end of the standalone authentication process, this is set to the JWT of the learner or student
    standaloneAuthUser: undefinedStandaloneAuthUser,
    isFirebaseConnected: false,
    // number of firebase disconnects encountered during the current session
    firebaseDisconnects: 0,
    isLoggingConnected: false,
    // number of logging disconnects encountered during the current session
    loggingDisconnects: 0,
    // number of network status alerts presented during the current session
    networkStatusAlerts: 0
}))
    .actions((self) => ({
    setName(name) {
        self.name = name;
    },
    setAuthenticated(auth) {
        self.authenticated = auth;
    },
    setClassName(className) {
        self.className = className;
    },
    setLatestGroupId(latestGroupId) {
        self.latestGroupId = latestGroupId;
    },
    setCurrentGroupId(currentGroupId) {
        self.currentGroupId = currentGroupId;
    },
    setId(id) {
        self.id = id;
    },
    setType(type) {
        self.type = type;
    },
    setNetworks(network, networks) {
        self.network = network;
        self.networks.push(...networks);
    },
    setAuthenticatedUser(user) {
        var _a;
        self.authenticated = true;
        self.name = user.fullName;
        self.latestGroupId = undefined;
        self.id = user.id;
        self.portal = user.portal;
        self.type = user.type;
        if (isAuthenticatedTeacher(user)) {
            self.network = user.network;
            if (user.networks) {
                self.networks.push(...user.networks);
            }
        }
        self.className = user.className;
        self.classHash = user.classHash;
        self.offeringId = user.offeringId;
        if (user.firebaseJWT && user.firebaseJWT.returnUrl) {
            self.loggingRemoteEndpoint = user.firebaseJWT.returnUrl;
        }
        if (user.portalClassOfferings) {
            self.portalClassOfferings.replace(user.portalClassOfferings);
        }
        if ((_a = user.demoClassHashes) === null || _a === void 0 ? void 0 : _a.length) {
            self.demoClassHashes.replace(user.demoClassHashes);
        }
    },
    setStandaloneAuth(value) {
        self.standaloneAuth = value;
    },
    setStandaloneAuthUser(value) {
        self.standaloneAuthUser = value;
    },
    setIsFirebaseConnected(connected) {
        if (self.isFirebaseConnected && !connected)
            ++self.firebaseDisconnects;
        self.isFirebaseConnected = connected;
    },
    setIsLoggingConnected(connected) {
        if (self.isLoggingConnected && !connected)
            ++self.loggingDisconnects;
        self.isLoggingConnected = connected;
    },
    incrementNetworkStatusAlertCount() {
        ++self.networkStatusAlerts;
    },
    setLastSupportViewTimestamp(timestamp) {
        self.lastSupportViewTimestamp = timestamp;
    },
    setLastStickyNoteViewTimestamp(timestamp) {
        self.lastStickyNoteViewTimestamp = timestamp;
    }
}))
    .views((self) => ({
    get isStudent() {
        return self.type === "student";
    },
    get isTeacher() {
        return self.type === "teacher";
    },
    get isResearcher() {
        return self.type === "researcher";
    },
    get isTeacherOrResearcher() {
        return (self.type === "teacher") || (self.type === "researcher");
    },
    get isNetworkedTeacher() {
        return (self.type === "teacher") && !!self.network;
    },
    get initials() {
        return initials(self.name);
    },
    get activityUrl() {
        const offering = self.portalClassOfferings.find(o => o.offeringId === self.offeringId);
        return offering === null || offering === void 0 ? void 0 : offering.activityUrl;
    },
    /**
     * Group documents have a special user id based on the offering and group id.
     * In this way every member of the group will have the same userId for group documents
     *
     * @returns
     */
    get userIdForGroupDocuments() {
        return `group_${self.offeringId}_${self.currentGroupId}`;
    }
}))
    .views((self) => ({
    classHashesForProblemPath(problemPath) {
        const classSet = new Set([self.classHash]);
        if (self.isTeacher) {
            // authenticated teachers pull class hashes from portalClassOfferings
            if (self.portalClassOfferings.length) {
                self.portalClassOfferings.forEach(o => {
                    if (o.classHash && (o.problemPath === problemPath)) {
                        classSet.add(o.classHash);
                    }
                });
            }
            // non-authenticated teachers (e.g. demo/qa) pull from demoClassHashes
            else if (self.demoClassHashes.length) {
                self.demoClassHashes.forEach(hash => classSet.add(hash));
            }
        }
        return [...classSet];
    },
}));
//# sourceMappingURL=data:application/json;base64,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